import { View } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { memo, useEffect, useMemo, useState } from 'react';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { deleteFile, deleteFolder, saveFiles, updateFile } from "@/data/library"
import { TouchableOpacity } from 'react-native';
import ActionCard from '@/components/General/ActionCard';
import { formatSize, getFile, removeStorageFile, saveFile } from "@/utils/FileManager"
import * as DocumentPicker from 'expo-document-picker';
import FileViewer from "react-native-file-viewer";
import Toast from 'react-native-toast-message'
import { LegendList } from "@legendapp/list";
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import useLoader from "@/utils/hooks/useLoader"
import Input from '@/components/General/inputs/Input';
import { showAlert } from '@/utils/showAlert';
import SheetModal from '@/components/General/SheetModal';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import DropdownMenu from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';


export default function FolderScreen(){

    const { folderID, path, titleOverwrite } = useLocalSearchParams();
    
    /*** State ***/
    const [renamedFile, setRenamedFile] = useState({});
    const [progress, setProgress] = useState(0);
    const [savingFilename, setSavingFilename] = useState(false);
    const [newFolderModalOpen, setNewFolderModalOpen] = useState(false);
    const [renameModalOpen, setRenameModalOpen] = useState(false);

    
    /*** Theme & Dimensions ***/
    const router = useRouter();
    const navigation = useNavigation();

    if(!folderID){
        router.canGoBack() ? router.back() : router.replace("/")
    }

    const snapPoints = useMemo(() => [400], []);

    const loader = useLoader();
    const isRoot = folderID == 'home' && path == "resources/";

    /*** Animations ***/


    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: loggedInUser } = Query.people.loggedInUser()

    const { data: folder, isFetching } = isRoot
        ? Query.folders.byRootFolder() 
        : Query.folders.byId({id: folderID})

    const saveFileMutation = useMutation({
        mutationFn: saveFiles,
        onSuccess: async () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.folders.all()});

            setProgress(0);
            setNewFolderModalOpen(false);
        },
        onError: (err) => {
            setProgress(0);
            console.debug(err)
        },
    })

    const updateFileMutation = useMutation({
        mutationFn: updateFile,
        onSuccess: async () => {
            queryClient.invalidateQueries(isRoot ? ['folders', 'ROOT'] : ['folders', folderID]);
            setSavingFilename(false);
            setRenamedFile({});
            setRenameModalOpen(false);
        }
    })

    const deleteFileMutation = useMutation({
        mutationFn: deleteFile,
        onMutate: ({fileID, key}) => {
            return {fileID, key}
        },
        onSuccess: async (data, variables, context) => {
            removeStorageFile(context.key)

            queryClient.invalidateQueries(isRoot ? ['folders', 'ROOT'] : ['folders', folderID]);

            setProgress(0);
            setNewFolderModalOpen(false);
        },
        onError: (err) => {
            setProgress(0);
            console.debug(err)
        },
    })

    const deleteFolderMutation = useMutation({
        mutationFn: deleteFolder,
        onMutate: () => {
            loader.enable();
        },
        onSuccess: async () => {
            loader.disable();
            queryClient.invalidateQueries(isRoot ? ['folders', 'ROOT'] : ['folders', folderID]);
        }
    })


    const fileIcons = useMemo(() => ({
        "folder": <Unicon library="fa5"  name="folder" colorVariant="textSecondary" size={32} solid />,
        ".jpg": <Unicon library="fa5"  name="file-image" style={{color: "#04bdc7"}} size={32} solid />,
        ".png": <Unicon library="fa5"  name="file-image" style={{color: "#04bdc7"}} size={32} solid />,
        ".jpeg": <Unicon library="fa5"  name="file-image" style={{color: "#04bdc7"}} size={32} solid />,
        ".gif": <Unicon library="fa5"  name="file-image" style={{color: "#04bdc7"}} size={32} solid />,
        ".mp4": <Unicon library="fa5"  name="file-video" style={{color: "#b3141c"}} size={32} solid />,
        ".mov": <Unicon library="fa5"  name="file-video" style={{color: "#b3141c"}} size={32} solid />,
        ".avi": <Unicon library="fa5"  name="file-video" style={{color: "#b3141c"}} size={32} solid />,
        ".mp3": <Unicon library="fa5"  name="file-audio" style={{color: "#8531bd"}} size={32} solid />,
        ".wav": <Unicon library="fa5"  name="file-audio" style={{color: "#8531bd"}} size={32} solid />,
        ".wma": <Unicon library="fa5"  name="file-audio" style={{color: "#8531bd"}} size={32} solid />,
        ".m4a": <Unicon library="fa5"  name="file-audio" style={{color: "#8531bd"}} size={32} solid />,
        ".xls": <Unicon library="fa5"  name="file-excel" style={{color: "#1d6f42"}} size={32} solid />,
        ".xlsx": <Unicon library="fa5"  name="file-excel" style={{color: "#1d6f42"}} size={32} solid />,
        ".xlsm": <Unicon library="fa5"  name="file-excel" style={{color: "#1d6f42"}} size={32} solid />,
        ".numbers": <Unicon library="fa5"  name="file-excel" style={{color: "#1d6f42"}} size={32} solid />,
        ".csv": <Unicon library="fa5"  name="file-csv" style={{color: "#1d6f42"}} size={32} solid />,
        ".docx": <Unicon library="fa5"  name="file-word" style={{color: "#0078d7"}} size={32} solid />,
        ".doc": <Unicon library="fa5"  name="file-word" style={{color:"#0078d7"}} size={32} solid />,
        ".docm": <Unicon library="fa5"  name="file-word" style={{color:"#0078d7"}} size={32} solid />,
        ".pages": <Unicon library="fa5"  name="file-word" style={{color:"#0078d7"}} size={32} solid />,
        ".pptx": <Unicon library="fa5"  name="file-powerpoint" style={{color:"#f65314"}} size={32} solid />,
        ".pptm": <Unicon library="fa5"  name="file-powerpoint" style={{color:"#f65314"}} size={32} solid />,
        ".ppt": <Unicon library="fa5"  name="file-powerpoint" style={{color:"#f65314"}} size={32} solid />,
        ".key": <Unicon library="fa5"  name="file-powerpoint" style={{color:"#f65314"}} size={32} solid />,
        ".pdf": <Unicon library="fa5"  name="file-pdf" style={{color:"#f40f02"}} size={32} solid />,
        ".html": <Unicon library="fa5"  name="file-code" style={{color:"#F0DB4F"}} size={32} solid />,
        ".js": <Unicon library="fa5"  name="file-code" style={{color:"#F0DB4F"}} size={32} solid />,
        ".css": <Unicon library="fa5"  name="file-code" style={{color:"#F0DB4F"}} size={32} solid />,
        ".sql": <Unicon library="fa5"  name="file-code" style={{color:"#F0DB4F"}} size={32} solid />,
        ".xml": <Unicon library="fa5"  name="file-code" style={{color:"#F0DB4F"}} size={32} solid />,
        ".py": <Unicon library="fa5"  name="file-code" style={{color:"#F0DB4F"}} size={32} solid />,
        ".php": <Unicon library="fa5"  name="file-code" style={{color:"#F0DB4F"}} size={32} solid />,
        ".exe": <Unicon library="fa5"  name="file-code" style={{color:"#F0DB4F"}} size={32} solid />,
        ".zip": <Unicon library="fa5"  name="file-archive" colorVariant="textSecondary" size={32} solid />,
        ".pkg": <Unicon library="fa5"  name="file-archive" colorVariant="textSecondary" size={32} solid />,
        ".txt": <Unicon library="fa5"  name="file-alt" colorVariant="textSecondary" size={32} solid />,
        ".rtf": <Unicon library="fa5"  name="file-alt" colorVariant="textSecondary" size={32} solid />,
    }), [])


    useEffect(() => {
        navigation.setOptions({
            headerTitle: titleOverwrite ? titleOverwrite : (folder ? folder?.name : ""),
            headerRight: () => (
                <View style={{flexDirection:"row"}}>
                    <TouchableOpacity onPress={() => setNewFolderModalOpen(true)}>
                        <Unicon name="add-outline" size={32} colorVariant="text" />
                    </TouchableOpacity>
                </View>
            ),
        });
    }, [navigation, newFolderModalOpen, folder])


    const refreshList = () => {
        queryClient.invalidateQueries(isRoot ? ['folders', 'ROOT'] : ['folders', folderID]);
    }


    const RenderItem = memo(({item, _index}) => {
        const type = !item?.filename ? "folder" : "file";

        const size = formatSize(item?.size);

        const ext = type == "folder" ? type : ("." + item?.filename.split('.').pop());
        const name = type == "folder" ? item?.name : item?.filename
        const subtitle = type == "folder" ? null : size + " • " + item?.uploadedBy.firstName + " " + item?.uploadedBy.lastName

        const options = type == "folder" ? [
            {name: "Edit Folder", onSelect: () => {
                router.push({pathname: "/library/folders/create", params: {id: item.id}})
            }},
            {name: "Delete Folder", destructive: true, onSelect: () => showAlert({title: "Delete Folder?", subtitle: "Are you sure you want to delete this Folder? You will lose all of the files in the folder unless you back them up.", isDestructive: true, onConfirm: () => deleteFolderMutation.mutate(item.id), confirmText: "Delete"})}
        ] : [
            {name: "Rename File", onSelect: () => {
                setRenamedFile({id: item?.id, filename: item?.filename.replace(/\.[^/.]+$/, ""), ext: ext})
                setRenameModalOpen(true)
            }},
            {name: "Delete File", destructive: true, onSelect: () => showAlert({title: "Delete File?", subtitle: "Are you sure you want to delete this File? You will lose this file unless you have it backed up.", isDestructive: true, onConfirm: () => deleteFileMutation.mutate({fileID: item.id, key: item.key}), confirmText: "Delete"})}
        ]

        return <View>
            <ActionCard
                title={name}
                menu={<DropdownMenu options={options} />}
                subtitle={subtitle}
                timeStamp={item?.updatedAt}
                icon={fileIcons[ext] || <Unicon library="fa5"  name="file" colorVariant="textSecondary" size={32} solid />}
                enableSwipeLeft={false}
                enableSwipeRight={type == "file"}
                rightIcon="trash"
                rightAction={() => {
                    if(type == "file"){
                        showAlert({
                            title: "Delete File",
                            subtitle: "Are you sure you want to delete this file? You will lose this file unless you have it backed up.",
                            isDestructive: true,
                            onConfirm: () => deleteFileMutation.mutate({fileID: item.id, key: item.key}),
                            confirmText: "Delete"
                        })
                    }
                    else if(type == "folder"){
                        showAlert({
                            title: "Delete Folder",
                            subtitle: "Are you sure you want to delete this folder? You will lose all of the files in the folder unless you back them up.",
                            isDestructive: true,
                            onConfirm: () => deleteFolderMutation.mutate({folderID: item?.id}),
                            confirmText: "Delete"
                        })
                    }
                }}
                confirmAction={async () => {
                    // if(ext == ".docx"){
                    //     const file = await getFile((path + item?.filename))
                    //     // const file = await getStorageFileURL((path + item?.filename))

                    //     console.log(file);

                    //     const word_url =`ms-word://ofv|u|${encodeURI(file)}|d|ChurchApp|e|${loggedInUser?.id}|n|${item?.filename}|a|App`

                    //     Linking.canOpenURL(word_url).then((canOpen) => {
                    //         if(canOpen){
                    //             Linking.openURL(word_url)
                    //         }
                    //         else{
                    //             console.log("ope.. cant open")
                    //         }
                    //     });
                    // }


                    if(type == "folder"){
                        router.push({pathname: "/library/folders/" + item.id, params: {path: path + item?.name + "/"}})
                    }
                    else{
                        const file = await getFile((path + item?.filename))

                        if(file){
                            FileViewer.open(file, { showOpenWithDialog: true, showAppsSuggestions: true }).then(result => {
                                
                            });
                        }
                        else{
                            Toast.show({
                                type: 'error',
                                text2: `Failed to open file. It may be corrupted or missing`
                            });
                        }
                    }
                }}
            />
        </View>
    })


    return (
        <View style={{flex: 1, alignItems: 'stretch', justifyContent: 'center', padding:10, paddingTop:10 }}>

            <LegendList recycleItems keyExtractor={(item) => item.id}  
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                estimatedItemSize={105}
                data={isFetching ? [] : [...(folder?.subfolders || []), ...(folder?.files || [])]}
                removeClippedSubviews
                renderItem={({item, index}) => <RenderItem item={item} index={index}/>} 
                // onEndReached={fetchItems}
                // onEndReachedThreshold={0.1}
                windowSize={5}
                progressViewOffset={1000}
                refreshing={false}
                onRefresh={refreshList}
                // ListHeaderComponent={header}
                // stickyHeaderIndices={[0]}
                ListFooterComponent={!isFetching 
                    ? folder?.subfolders?.length == 0 && folder?.files?.length == 0 ? <EmptyListPlaceholder icon="folder-outline" spacing={10} message="This folder is empty, upload or create a new folder with the '+' icon!"/> : <View style={{paddingBottom:150}}></View>
                    : <View style={{alignContent:'center', justifyContent:'center'}}>
                        <ContentLoader />
                        <ContentLoader staggerPosition={1}/>
                        <ContentLoader staggerPosition={2}/>
                        <ContentLoader staggerPosition={3}/>
                        <ContentLoader staggerPosition={4}/>
                        <ContentLoader staggerPosition={5}/>
                    </View>
                }
            />


           
            <SheetModal isOpen={newFolderModalOpen} onClose={() => setNewFolderModalOpen(false)} title="" snapPoints={snapPoints}>
                <ActionCard
                    title="Create a New Folder"
                    icon={<Unicon library="fa5"  name="folder-plus" colorVariant="textSecondary" size={32} solid />}
                    enableSwipeLeft={false}
                    enableSwipeRight={false}
                    confirmAction={() => {
                        setNewFolderModalOpen(false);
                        router.push({pathname: "/library/folders/create", params: {parentFolderID: isRoot ? folder?.id : folderID}})
                    }}
                />

                <ActionCard
                    title="Upload from Photo Library"
                    icon={<Unicon library="fa5"  name="images" colorVariant="textSecondary" size={32} solid />}
                    enableSwipeLeft={false}
                    enableSwipeRight={false}
                    confirmAction={() => {
                        setNewFolderModalOpen(false);
                        router.push({pathname: "/library/upload", params: {parentFolderID: isRoot ? folder?.id : folderID, path: path}})
                    }}
                />

                <ActionCard
                    title="Upload a File"
                    icon={<Unicon library="fa5"  name="file-upload" colorVariant="textSecondary" size={32} solid />}
                    enableSwipeLeft={false}
                    enableSwipeRight={false}
                    confirmAction={async () => {
                        const picker = await DocumentPicker.getDocumentAsync();

                        if(!picker || !picker.assets){
                            return
                        }

                        const file = picker.assets[0];
                        const {key, newFilePath} = await saveFile(file.uri, `${loggedInUser?.organizationID}/${path}${file.name.trim()}`);

                        if(key){
                            saveFileMutation.mutate({files: [file], path: path, folderID: folderID, uploadedByID: loggedInUser?.id})
                        }
        
                    }}
                />

            </SheetModal>

            <SheetModal isOpen={renameModalOpen} onClose={() => setRenameModalOpen(false)} title="Rename File" snapPoints={[200, 520]} loading={savingFilename} confirmText="Confirm" onConfirm={() => { 
                setSavingFilename(true);
                updateFileMutation.mutate({id: renamedFile.id, newFilename: renamedFile.filename + renamedFile.ext})
            }}>
                <Input
                    title="File Name"
                    value={renamedFile.filename}
                    onUpdate={(new_value) => {
                        setRenamedFile(old => ({...old, filename: new_value}))
                    }}
                />

            </SheetModal>
        </View>
    )
}